import { useState, useEffect, useRef } from 'react' //, useCallback
import { AppBar, AppBarSection, AppBarSpacer } from '@progress/kendo-react-layout'
import TechPartnerDashboardMainFilterInputs from '../Components/TechPartnerDashboardMainFilterInputs'
import TechPartnerDashboardSection, {
    // TechPartnerDashboardSectionColor,
    TechPartnerDashboardSectionType,
} from '../Components/TechPartnerDashboardSection'
import { BUTTON_TYPES, AWCButton } from '../../GlobalComponents/Inputs/Inputs'
import {
    NotificationManager,
    fqAlert,
} from '../../GlobalComponents/NotificationManager/NotificationManager'

import './TechPartnerDashboard.scss'
import styles from './TechPartnerDashboard.module.scss'
import { VendorProductCodes } from '../../features/inventory/inventoryAPI'
import { SalesDivision, Salesperson } from '../../features/salesperson/salespersonAPI'
import { hasTechPartnerDashboardPermissions } from '../../features/meta/metaAPI'
import 'hammerjs'
import html2canvas from 'html2canvas'
import TechPartnerDashboardNavigationBar from '../Components/TechPartnerDashboardNavigationBar'

const calculateTopPosition = (elementRef: any) => {
    const rect = elementRef.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    return rect.bottom - 168
}

export interface TechPartnerDashboardInput {
    VendorProductCodes?: VendorProductCodes
    SalespersonCodes?: Salesperson[]
    SalesDivisions?: SalesDivision[]
    ReportPeriodBegin?: Date
    ReportPeriodEnd?: Date
}

export default function TechPartnerDashboard() {
    const [initialLoading, setInitialLoading] = useState(true)
    const [selectedInputs, setSelectedInputs] = useState<TechPartnerDashboardInput>({})
    const [selectedSalespeople, setSelectedSalespeople] = useState<Salesperson[]>([])
    const [selectedSalesDivisions, setSelectedSalesDivisions] = useState<SalesDivision[]>([])
    const [allowed, setAllowed] = useState<boolean>(false)

    const mainHeaderRef = useRef(null)
    const salesQuotaRef = useRef(null)
    const fulfillmentRef = useRef(null)
    const marketOpportunityRef = useRef(null)
    const teamResourcesRef = useRef(null)
    const salesProcessRef = useRef(null)
    const customersRef = useRef(null)

    const sectionRefs = [
        { section: 'Main Header', ref: mainHeaderRef },
        { section: 'Sales Plan', ref: salesQuotaRef },
        { section: 'Fulfillment', ref: fulfillmentRef },
        { section: 'Market Opportunity', ref: marketOpportunityRef },
        { section: 'Team Resources', ref: teamResourcesRef },
        { section: 'Sales Process', ref: salesProcessRef },
        { section: 'Customers', ref: customersRef },
    ]

    const initLoad = async () => {
        let hasPerms = await hasTechPartnerDashboardPermissions()
        setAllowed(hasPerms)
        setInitialLoading(false)
    }

    useEffect(() => {
        document.title = 'Technology Partner Dashboard'
        initLoad()
    }, [])

    const copyToClipboard = async (pngBlob: Blob) => {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [pngBlob.type]: pngBlob,
                }),
            ])
            fqAlert({ type: 'success', message: 'Dashboard copied successfully!' })
        } catch (error) {
            console.error(error)
        }
    }

    const exportDashboard = async () => {
        const mainContainer = document.querySelector(`.${styles.mainContainer}`)
        if (!mainContainer) return

        const canvas = await html2canvas(mainContainer as HTMLElement, {
            onclone: function (documentClone) {
                let headerDisclaimerClone = documentClone.querySelector(
                    `.${styles.headerDisclaimer}`
                ) as HTMLDivElement | null
                if (headerDisclaimerClone)
                    headerDisclaimerClone.innerText = 'Confidential - AWC Internal Only'
                documentClone
                    .querySelectorAll('.hide-on-export')
                    .forEach((el) => ((el as HTMLElement).style.visibility = 'hidden'))
            },
        })

        canvas.toBlob(
            async (blob) => {
                if (blob) await copyToClipboard(blob)
            },
            'image/png',
            1.0
        )
    }

    if (initialLoading) {
        return (
            <div className={styles.app}>
                <div className={'centered'}>
                    <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                </div>
                <NotificationManager key={'notificationsManager'} />
            </div>
        )
    }

    if (!allowed)
        return (
            <div
                className={`${styles.alert} ${styles.alertDanger}`}
                role='alert'
                style={{
                    textAlign: 'center',
                    marginTop: 60,
                }}
            >
                <h3>Permission Denied</h3>
                <p>Permission is required to use Tech Partner Dashboard!</p>
            </div>
        )

    return (
        <div className={`${styles.mainContainer} techPartner`}>
            <AppBar
                style={{ paddingBottom: 'unset' }}
                className={`${styles.appHeader} ${styles.headerContainer}`}
            >
                <div className={styles.headerTitle}>Technology Partner Dashboard</div>
                <div className={styles.headerDisclaimer}></div>
                <div className={styles.headerDate}>{new Date().toLocaleDateString()}</div>
                <div className={`${styles.headerExport} hide-on-export`}>
                    <AWCButton onClick={exportDashboard} type={BUTTON_TYPES.PRIMARY}>
                        Copy All
                    </AWCButton>
                </div>
            </AppBar>
            <TechPartnerDashboardNavigationBar
                selectedInputs={selectedInputs}
                sectionRefs={sectionRefs}
            />
            <TechPartnerDashboardMainFilterInputs
                selectedInputs={selectedInputs}
                onSelectedValuesChanged={(selectedInputs: TechPartnerDashboardInput) => {
                    setSelectedInputs(selectedInputs)
                }}
                onSelectedSalespeopleChanged={(selectedSalespeople: Salesperson[]) => {
                    setSelectedSalespeople(selectedSalespeople)
                }}
                onSelectedSalesDivisionsChanged={(selectedSalesDivisions: SalesDivision[]) => {
                    setSelectedSalesDivisions(selectedSalesDivisions)
                }}
                mainHeaderRef={mainHeaderRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.SALESQUOTA}
                selectedInputs={selectedInputs}
                selectedSalespeople={selectedSalespeople}
                selectedSalesDivisions={selectedSalesDivisions}
                sectionRef={salesQuotaRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.FULFILLMENTPROCESS}
                selectedInputs={selectedInputs}
                sectionRef={fulfillmentRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.MARKETOPPORTUNITY}
                selectedInputs={selectedInputs}
                sectionRef={marketOpportunityRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.TEAMRESOURCES}
                selectedInputs={selectedInputs}
                sectionRef={teamResourcesRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.SALESPROCESS}
                selectedInputs={selectedInputs}
                sectionRef={salesProcessRef}
            />
            <TechPartnerDashboardSection
                type={TechPartnerDashboardSectionType.CUSTOMERS}
                selectedInputs={selectedInputs}
                sectionRef={customersRef}
            />
            <NotificationManager key={'notificationsManager'} />
        </div>
    )
}
